import React from "react";
import {lighten} from "@mui/material";
import {createTheme, PaletteColor, PaletteColorOptions } from "@mui/material/styles";

declare module "@mui/material/styles/createTheme" {
  interface Theme {
    extras: {
      logo: string;
      gradientStartColor: React.CSSProperties["color"];
      gradientEndColor: React.CSSProperties["color"];
      overlayColor: React.CSSProperties["color"];
      additionalStylesFile: string;
      boolTrue: React.CSSProperties["color"];
      boolFalse: React.CSSProperties["color"];
    };
  }

  interface ThemeOptions {
    extras?: {
      logo?: string;
      gradientStartColor?: React.CSSProperties["color"];
      gradientEndColor?: React.CSSProperties["color"];
      overlayColor?: React.CSSProperties["color"];
      additionalStylesFile?: string;
      boolTrue?: React.CSSProperties["color"];
      boolFalse?: React.CSSProperties["color"];
    };
  }
}

export const darkTheme = createTheme({
  palette: {
    primary: {
      light: "#1DACD6",
      main: "#1DACD6",
      dark: "#1DACD6",
      contrastText: "#f5f6fa",
    },
    secondary: {
      light: "#FE5E55",
      main: "#FE5E55",
      dark: "#FE5E55",
      contrastText: "#f5f6fa",
    },
    text: {
      primary: "#f5f6fa",
      secondary: "#f5f6faAA",
    },
    background: {
      default: "#25262E",
      paper: "#2D3038"
    },
    success: {
      main: "#2CD58E",
    },
    info: {
      main: "#2CD58E",
    },
    error: {
      main: "#FE5E55",
      dark: "#601b17",
    },
    highlight: {
      main: "#fff176",
      dark: "#fdd835",
      light: "#ffff8d"
    }
  },
  spacing: [2, 4, 8, 12, 16],
  typography: {
    fontSize: 14,
    // fontFamily: `"JakartaSans", sans-serif`,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 400,
    fontWeightBold: 400,
    body1: {
      fontSize: 14,
    },
    body2: {
      fontSize: 14,
    },
    button: {
      fontSize: 14,
    },
    h4: {
      fontSize: 28,
    },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "0 !important",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          boxShadow: "none !important",
          textTransform: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "20px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "0px 20px 0px 20px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 20,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 20,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        sizeSmall: {
          padding: "6px 10px 6px 10px",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: "none !important",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderBottom: "none",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: 10,
          borderRadius: 10,
        },
        list: {
          padding: "0 !important",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: "10px !important",
          "&:hover": {
            backgroundColor: "#FAFAFD",
          },
          "&$selected": {
            backgroundColor: "#FAFAFD",
          },
        },
      },
    },
    // @ts-ignore
    MuiTreeItem: {
      styleOverrides: {
        label: {
          backgroundColor: "rgba(0, 0, 0, 0.0) !important",
          fontWeight: "bold !important",
          fontSize: "14px",
        },
        group: {
          marginLeft: "22px",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: "rgba(0, 0, 0, 0.0) !important",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0)",
          borderRadius: 10,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: "#2D3038",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: "2px solid #2D3038",
          "Mui-selected": {
            backgroundColor: "#ff0000 !important",
          },
        },
      },
    },
  },
  extras: {
    gradientStartColor: lighten("#263238", 0.04),
    gradientEndColor: lighten("#263238", 0.04),
    overlayColor: "#263238",
    additionalStylesFile: "./themes/darkThemeAdditionalStyles.css",
    boolTrue: '#59A14F',
    boolFalse: '#D66A4E'
  },
});


export const lightTheme = createTheme({
  palette: {
    primary: {
      light: "#1DACD6",
      main: "#1DACD6",
      dark: "#1DACD6",
      contrastText: "#f5f6fa",
    },
    secondary: {
      light: "#FE5E55",
      main: "#FE5E55",
      dark: "#FE5E55",
      contrastText: "#f5f6fa",
    },
    text: {
      primary: "#222222",
      secondary: "#6d6d6d",
    },
    background: {
      default: "#d6d6d6",
      paper: "#d6d6d6",
    },
    success: {
      main: "#2CD58E",
    },
    info: {
      main: "#2CD58E",
    },
    error: {
      main: "#FE5E55",
      dark: "#601b17",
    },
    highlight: {
      main: "#fff176",
      dark: "#fdd835",
      light: "#ffff8d"
    }
  },
  spacing: [2, 4, 8, 12, 16],
  typography: {
    fontSize: 14,
    // fontFamily: `"JakartaSans", sans-serif`,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 400,
    fontWeightBold: 400,
    body1: {
      fontSize: 14,
    },
    body2: {
      fontSize: 14,
    },
    button: {
      fontSize: 14,
    },
    h4: {
      fontSize: 28,
    },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "0 !important",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          boxShadow: "none !important",
          textTransform: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "20px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "0px 20px 0px 20px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 20,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 20,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        sizeSmall: {
          padding: "6px 10px 6px 10px",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: "none !important",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderBottom: "none",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: 10,
          borderRadius: 10,
        },
        list: {
          padding: "0 !important",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: "10px !important",
          "&:hover": {
            backgroundColor: "#FAFAFD",
          },
          "&$selected": {
            backgroundColor: "#FAFAFD",
          },
        },
      },
    },
    // @ts-ignore
    MuiTreeItem: {
      styleOverrides: {
        label: {
          backgroundColor: "rgba(0, 0, 0, 0.0) !important",
          fontWeight: "bold !important",
          fontSize: "14px",
        },
        group: {
          marginLeft: "22px",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: "rgba(0, 0, 0, 0.0) !important",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0)",
          borderRadius: 10,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: "#2D3038",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: "2px solid #2D3038",
          "Mui-selected": {
            backgroundColor: "#ff0000 !important",
          },
        },
      },
    },
  },
  extras: {
    gradientStartColor: lighten("#263238", 0.04),
    gradientEndColor: lighten("#263238", 0.04),
    overlayColor: "#263238",
    additionalStylesFile: "./themes/darkThemeAdditionalStyles.css",
    boolTrue: '#59A14F',
    boolFalse: '#D66A4E'
  },
});
